import { apolloApi } from "@/shared/api";
import { useAccount, useAsync } from "@/shared/hooks";
import {
  Flex,
  Grid,
  Skeleton,
  SkeletonHeading,
  Text,
} from "@suns/design-system";
import { PlayerMedical, TempNbaPlayerStats } from "./components";
import { AiEditor } from "..";
import ApolloGradeChart from "../ApolloGradeChart";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import {
  PlayerMetadataRow,
  StatsQueryParams,
} from "@suns/api/generated-client/apollo";
import { ApolloStatsTable } from "@/components/ApolloStatsTable/ApolloStatsTable";
import {
  StatsGrouping,
  StatsObject,
} from "@/components/ApolloStatsTable/const";

interface PlayerOverviewParams {
  playerId: number;
  isNbaPlayer: boolean;
  metadata?: PlayerMetadataRow;
  nbaId?: number; // REMOVE THIS ONCE NBA PLAYERS ARE SUPPORTED BY ApolloStatsTable
  print?: boolean;
}

async function useOverviewLoader(playerId: number) {
  const { summaries } = await apolloApi.getPlayerSummaries(playerId);
  const { medical } = await apolloApi.getPlayerMedical(playerId);
  const performance = await apolloApi.getPlayerPerformanceMetrics(playerId);

  const filteredSummaries = summaries.filter(({ size }) => size === "SHORT");

  return {
    offense: filteredSummaries.find(({ type }) => type === "OFFENSE"),
    defense: filteredSummaries.find(({ type }) => type === "DEFENSE"),
    intel: filteredSummaries.find(({ type }) => type === "INTEL"),
    medical,
    performance,
  };
}

export function PlayerOverview({
  playerId,
  nbaId,
  metadata,
  isNbaPlayer,
  print = false,
}: PlayerOverviewParams) {
  const { isScoutAdmin, isDeveloper } = useAccount();

  const hasEditAccess = isScoutAdmin || isDeveloper;

  const { response, loading, error } = useAsync(useOverviewLoader, playerId);

  if (error) {
    throw error;
  }

  if (loading) {
    return <PlayerOverviewSkeleton />;
  }

  return (
    <Flex direction="down" gap={print ? "md" : "xl"}>
      {metadata && <ApolloGradeChart metadata={metadata} />}

      {nbaId && isNbaPlayer ? (
        <TempNbaPlayerStats nbaId={nbaId} print={print} />
      ) : (
        <ApolloStatsTable
          object={StatsObject.PLAYER}
          grouping={StatsGrouping.SEASON}
          careerRow={false}
          formatSelect={false}
          size={print ? "sm" : "md"}
          params={{
            playerId,
            seasonType: print
              ? StatsQueryParams.seasonType.REGULAR_SEASON
              : undefined,
          }}
        />
      )}

      <Grid columns={print ? ["2"] : ["1", "lg:2"]} gap="md">
        <AiEditor
          title="Offense"
          defaultValue={response?.offense?.value}
          readonly={print || !hasEditAccess}
          size="sm"
          onGenerateAi={async () => {
            const result = await apolloApi.generatePlayerSummary({
              playerId,
              type: "OFFENSE",
              size: "SHORT",
            });

            return result;
          }}
          onSave={async (value) => {
            await apolloApi.updatePlayerSummary({
              playerId,
              type: "OFFENSE",
              size: "SHORT",
              value,
            });

            return true;
          }}
          className={print ? "text-2xs" : ""}
        />
        <AiEditor
          title="Defense"
          defaultValue={response?.defense?.value}
          readonly={print || !hasEditAccess}
          size="sm"
          onGenerateAi={async () => {
            const result = await apolloApi.generatePlayerSummary({
              playerId,
              type: "DEFENSE",
              size: "SHORT",
            });

            return result;
          }}
          onSave={async (value) => {
            await apolloApi.updatePlayerSummary({
              playerId,
              type: "DEFENSE",
              size: "SHORT",
              value,
            });

            return true;
          }}
          className={print ? "text-2xs" : ""}
        />

        <AiEditor
          title="Intel"
          maxLength={1200}
          size="lg"
          defaultValue={response?.intel?.value}
          readonly={print || !hasEditAccess}
          onGenerateAi={async () => {
            const result = await apolloApi.generatePlayerSummary({
              playerId,
              type: "INTEL",
              size: "SHORT",
            });

            return result;
          }}
          onSave={async (value) => {
            await apolloApi.updatePlayerSummary({
              playerId,
              type: "INTEL",
              size: "SHORT",
              value,
            });

            return true;
          }}
          className={print ? "text-2xs" : ""}
        />
        {response?.medical?.notes ? (
          <Flex direction="down" gap="sm">
            <PlayerMedical
              print={print}
              medical={response.medical}
              title="Medical Analysis"
            />
          </Flex>
        ) : response?.performance?.summary ? (
          <Flex direction="down" gap="sm">
            <Text heading muted>
              Performance
            </Text>
            <Text className={print ? "text-2xs" : ""}>
              {response.performance.summary}
            </Text>
          </Flex>
        ) : null}
      </Grid>
    </Flex>
  );
}

function PlayerOverviewSkeleton() {
  return (
    <Grid columns="2" gap="md">
      <Flex direction="down" gap="sm">
        <SkeletonHeading />
        <Skeleton className="h-[200px] w-full" />
      </Flex>
      <Flex direction="down" gap="sm">
        <SkeletonHeading />
        <Skeleton className="h-[200px] w-full" />
      </Flex>
    </Grid>
  );
}
